import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Calendar, Clock, TrendingUp, CheckCircle2, AlertCircle } from 'lucide-react';

export const RecoveryCalculator = () => {
  const [condition, setCondition] = useState('');
  const [treatment, setTreatment] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [timeline, setTimeline] = useState(null);

  const recoveryData = {
    'rotator-cuff': {
      conservative: {
        total: 12,
        phases: [
          { name: 'Initial Rest & Pain Management', weeks: '0-2', activities: ['Rest', 'Ice therapy', 'Pain medications', 'Sling if needed'] },
          { name: 'Gentle Range of Motion', weeks: '2-6', activities: ['Pendulum exercises', 'Passive stretching', 'Physiotherapy begins'] },
          { name: 'Active Strengthening', weeks: '6-12', activities: ['Active exercises', 'Resistance training', 'Functional movements'] }
        ]
      },
      surgical: {
        total: 24,
        phases: [
          { name: 'Immobilization', weeks: '0-6', activities: ['Sling use 24/7', 'Passive motion only', 'Ice and elevation'] },
          { name: 'Active Assisted Motion', weeks: '6-12', activities: ['Remove sling', 'Active-assisted exercises', 'Progressive stretching'] },
          { name: 'Strengthening', weeks: '12-18', activities: ['Resistance exercises', 'Rotator cuff strengthening', 'Scapular stability'] },
          { name: 'Return to Activities', weeks: '18-24', activities: ['Sport-specific training', 'Full range strengthening', 'Gradual return to sports'] }
        ]
      }
    },
    'frozen-shoulder': {
      conservative: {
        total: 52,
        phases: [
          { name: 'Freezing Stage (Painful)', weeks: '0-16', activities: ['Pain management', 'Gentle stretching', 'Avoid aggravating movements'] },
          { name: 'Frozen Stage (Stiff)', weeks: '16-36', activities: ['Aggressive stretching', 'Physiotherapy daily', 'Heat therapy'] },
          { name: 'Thawing Stage (Recovery)', weeks: '36-52', activities: ['Progressive strengthening', 'Functional activities', 'Return to normal'] }
        ]
      },
      surgical: {
        total: 16,
        phases: [
          { name: 'Immediate Post-Op', weeks: '0-2', activities: ['Pain management', 'Immediate physiotherapy', 'Aggressive stretching'] },
          { name: 'Restoration of Motion', weeks: '2-8', activities: ['Daily physiotherapy', 'Full range exercises', 'Prevent re-stiffening'] },
          { name: 'Strengthening', weeks: '8-16', activities: ['Resistance training', 'Functional exercises', 'Return to activities'] }
        ]
      }
    },
    'shoulder-dislocation': {
      conservative: {
        total: 16,
        phases: [
          { name: 'Immobilization', weeks: '0-3', activities: ['Sling use', 'Ice therapy', 'Rest'] },
          { name: 'Range of Motion', weeks: '3-8', activities: ['Gentle stretching', 'Pendulum exercises', 'Avoid stress positions'] },
          { name: 'Strengthening', weeks: '8-16', activities: ['Rotator cuff exercises', 'Scapular stability', 'Progressive loading'] }
        ]
      },
      surgical: {
        total: 24,
        phases: [
          { name: 'Protection Phase', weeks: '0-6', activities: ['Sling 24/7', 'Passive motion only', 'No external rotation'] },
          { name: 'Active Motion', weeks: '6-12', activities: ['Active exercises', 'Progressive stretching', 'Light strengthening'] },
          { name: 'Strengthening', weeks: '12-18', activities: ['Resistance training', 'Sport drills', 'Stability exercises'] },
          { name: 'Return to Sport', weeks: '18-24', activities: ['Full activities', 'Contact sports clearance', 'Maintenance program'] }
        ]
      }
    },
    'shoulder-replacement': {
      surgical: {
        total: 52,
        phases: [
          { name: 'Early Recovery', weeks: '0-6', activities: ['Sling use', 'Passive exercises', 'Wound healing'] },
          { name: 'Active Motion', weeks: '6-12', activities: ['Active exercises', 'Daily activities', 'Light household tasks'] },
          { name: 'Strengthening', weeks: '12-26', activities: ['Progressive strengthening', 'Driving', 'Most activities'] },
          { name: 'Full Recovery', weeks: '26-52', activities: ['Maximum improvement', 'Low-impact sports', 'Full function'] }
        ]
      }
    }
  };

  const calculateRecovery = () => {
    if (condition && treatment) {
      const data = recoveryData[condition]?.[treatment];
      if (data) {
        setTimeline(data);
        setShowTimeline(true);
      }
    }
  };

  const reset = () => {
    setCondition('');
    setTreatment('');
    setShowTimeline(false);
    setTimeline(null);
  };

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recovery Timeline Calculator
            </h2>
            <p className="text-lg text-gray-600">
              Get an estimated recovery timeline for your shoulder condition
            </p>
            <p className="text-sm text-gray-500 mt-2 flex items-center justify-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Timelines are estimates; actual recovery varies by individual
            </p>
          </div>

          {/* Input Card */}
          {!showTimeline && (
            <Card>
              <CardHeader>
                <CardTitle>Select Your Condition & Treatment</CardTitle>
                <CardDescription>Choose to see your personalized recovery timeline</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Shoulder Condition
                  </label>
                  <Select value={condition} onValueChange={setCondition}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rotator-cuff">Rotator Cuff Tear</SelectItem>
                      <SelectItem value="frozen-shoulder">Frozen Shoulder</SelectItem>
                      <SelectItem value="shoulder-dislocation">Shoulder Dislocation</SelectItem>
                      <SelectItem value="shoulder-replacement">Shoulder Replacement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Treatment Type
                  </label>
                  <Select value={treatment} onValueChange={setTreatment} disabled={!condition}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select treatment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {condition !== 'shoulder-replacement' && (
                        <SelectItem value="conservative">Conservative (Non-Surgical)</SelectItem>
                      )}
                      <SelectItem value="surgical">Surgical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={calculateRecovery}
                  disabled={!condition || !treatment}
                  className="w-full bg-medical-orange hover:bg-orange-600 text-white"
                  size="lg"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Calculate Recovery Timeline
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Timeline Display */}
          {showTimeline && timeline && (
            <div className="space-y-6">
              {/* Overview */}
              <Card className="border-l-4 border-l-medical-orange">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-medical-orange" />
                    Total Recovery Time: {timeline.total} weeks ({Math.floor(timeline.total / 4)} months)
                  </CardTitle>
                  <CardDescription>
                    {treatment === 'surgical' ? 'Post-surgical' : 'Conservative treatment'} recovery timeline
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Phases */}
              {timeline.phases.map((phase, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <CardTitle className="text-xl mb-1">Phase {index + 1}: {phase.name}</CardTitle>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Weeks {phase.weeks}
                        </p>
                      </div>
                      <div className="text-medical-orange font-bold text-lg">
                        {Math.round((index + 1) / timeline.phases.length * 100)}%
                      </div>
                    </div>
                    <Progress value={(index + 1) / timeline.phases.length * 100} className="h-2" />
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Key Activities:
                    </p>
                    <ul className="space-y-2">
                      {phase.activities.map((activity, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-medical-orange mt-1">•</span>
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}

              {/* Actions */}
              <div className="flex gap-4">
                <Button onClick={reset} variant="outline" className="flex-1">
                  Calculate Another
                </Button>
                <a href="https://drdnreddyortho.com/#shoulder" target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-medical-orange hover:bg-orange-600 text-white">
                    Get Personalized Plan
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};